import type { OrganizationType } from "@gately/validators";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ChevronRight, Plus, Trash2 } from "lucide-react";

import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { Switch } from "@gately/ui/switch";
import { toast } from "@gately/ui/toast";
import { getTerms } from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { slugify } from "~/lib/slugify";
import { resolvePermissions } from "~/lib/use-permissions";
import { useSwitchOrg } from "~/lib/use-switch-org";
import { capitalize } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/organizations")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).isOwner) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.organization.list.queryOptions()),
  component: OrganizationsPage,
});

/** Row label for the org's vertical — each immeuble named as what it is. */
function typeLabel(type: string): string {
  return type === "office" ? "Bureaux" : "Résidence";
}

function fmtDate(d: Date | string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** A labelled permission toggle row used in the create and edit dialogs. */
function PermToggle({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-neutral-200 px-4 py-3">
      <div>
        <Label htmlFor={id}>{label}</Label>
        <p className="text-[12px] text-neutral-400">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

/** Plateforme › Organisations › Modules — one switch per `ORG_FEATURES`
 *  entry. v1 has exactly one: Réservations d'équipements. */
function OrgModules({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const features = useQuery(
    orpc.organization.features.queryOptions({ input: { organizationId: orgId } }),
  );
  const set = useMutation(
    orpc.organization.setFeature.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: orpc.organization.features.key() });
        void qc.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: () => toast.error("Impossible de modifier le module."),
    }),
  );
  const on = features.data?.includes("amenities") ?? false;
  return (
    <div className="flex items-center justify-between gap-4 rounded-[12px] border border-neutral-200 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-neutral-950">
          Réservations d'équipements
        </p>
        <p className="text-[12px] text-neutral-400">
          Salles, coworking, terrains… réservables par les occupants.
        </p>
      </div>
      <Switch
        checked={on}
        disabled={features.isLoading || set.isPending}
        onCheckedChange={(enabled) =>
          set.mutate({ organizationId: orgId, feature: "amenities", enabled })
        }
        aria-label="Activer les réservations d'équipements"
      />
    </div>
  );
}

function OrganizationsPage() {
  const queryClient = useQueryClient();
  const switchOrg = useSwitchOrg();
  const { data: orgs } = useSuspenseQuery(
    orpc.organization.list.queryOptions(),
  );
  // The list is every résidence system-wide, but setActive requires membership.
  // Only orgs the owner belongs to are switch-into-able; mark the active one.
  const { data: me } = useSuspenseQuery(orpc.me.get.queryOptions());
  const memberIds = new Set(me.memberships.map((m) => m.orgId));

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [type, setType] = useState<"residence" | "office">("residence");
  const [viewAllPasses, setViewAllPasses] = useState(true);
  const [viewAllActivity, setViewAllActivity] = useState(true);

  // Permissions edit dialog (for an existing résidence).
  const [editing, setEditing] = useState<null | {
    id: string;
    name: string;
    type: string;
    viewAllPasses: boolean;
    viewAllActivity: boolean;
  }>(null);

  // Delete-confirmation dialog (Gately staff only). Type-to-confirm: the
  // exact org name must be typed before the destructive button enables.
  const [toDelete, setToDelete] = useState<null | { id: string; name: string }>(
    null,
  );
  const [confirmName, setConfirmName] = useState("");

  // Modules dialog (for an existing organisation).
  const [managingModules, setManagingModules] = useState<null | {
    id: string;
    name: string;
  }>(null);

  // Rename dialog.
  const [renaming, setRenaming] = useState<null | { id: string; name: string }>(
    null,
  );
  const [renameValue, setRenameValue] = useState("");
  // Suspend confirmation.
  const [toSuspend, setToSuspend] = useState<null | {
    id: string;
    name: string;
    type: string;
  }>(null);

  // Each dialog speaks in the words of the org it is about — an office has an
  // admin, responsables and agents d'accueil, not a syndic and concierges.
  const createTerms = getTerms(type);
  const editTerms = getTerms(editing?.type as OrganizationType | undefined);
  const suspendTerms = getTerms(
    toSuspend?.type as OrganizationType | undefined,
  );

  const nameValid = name.trim().length >= 1 && name.trim().length <= 120;
  const slugValid =
    /^[a-z0-9-]+$/.test(slug) && slug.length >= 2 && slug.length <= 40;

  function openDialog() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setSlugError(null);
    setType("residence");
    setViewAllPasses(true);
    setViewAllActivity(true);
    setOpen(true);
  }

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  function onSlugChange(value: string) {
    setSlugTouched(true);
    setSlugError(null);
    setSlug(value);
  }

  const create = useMutation(
    orpc.organization.create.mutationOptions({
      onSuccess: async (row) => {
        toast.success("Organisation créée.");
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
        setOpen(false);
        try {
          await switchOrg(row.id);
        } catch {
          toast.error(
            "Organisation créée — bascule impossible, sélectionnez-la dans le menu.",
          );
        }
      },
      onError: (err) => {
        const e = err as { code?: string; message?: string };
        if (e.code === "CONFLICT") {
          setSlugError(e.message ?? "Ce slug est déjà utilisé.");
        } else {
          toast.error(e.message ?? "Impossible de créer l'organisation.");
        }
      },
    }),
  );

  const updatePerms = useMutation(
    orpc.organization.updatePermissions.mutationOptions({
      onSuccess: async () => {
        toast.success("Permissions mises à jour.");
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
        setEditing(null);
      },
      onError: (err) =>
        toast.error(
          (err as { message?: string }).message ?? "Échec de la mise à jour.",
        ),
    }),
  );

  const deleteOrg = useMutation(
    orpc.organization.delete.mutationOptions({
      onSuccess: async () => {
        toast.success("Organisation programmée pour suppression.");
        setToDelete(null);
        setConfirmName("");
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
      },
      onError: (err) =>
        toast.error(
          (err as { message?: string }).message ??
            "Échec de la suppression de l'organisation.",
        ),
    }),
  );

  const renameOrg = useMutation(
    orpc.organization.rename.mutationOptions({
      onSuccess: async () => {
        toast.success("Organisation renommée.");
        setRenaming(null);
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
        await queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) =>
        toast.error(
          (err as { message?: string }).message ?? "Échec du renommage.",
        ),
    }),
  );

  const suspendOrg = useMutation(
    orpc.organization.suspend.mutationOptions({
      onSuccess: async () => {
        toast.success("Organisation suspendue.");
        setToSuspend(null);
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
      },
      onError: (err) =>
        toast.error(
          (err as { message?: string }).message ?? "Échec de la suspension.",
        ),
    }),
  );

  const activateOrg = useMutation(
    orpc.organization.activate.mutationOptions({
      onSuccess: async () => {
        toast.success("Organisation réactivée.");
        await queryClient.invalidateQueries({
          queryKey: orpc.organization.list.key(),
        });
      },
      onError: (err) =>
        toast.error(
          (err as { message?: string }).message ?? "Échec de la réactivation.",
        ),
    }),
  );

  function openEdit(o: {
    id: string;
    name: string;
    type: string;
    permissions: { viewAllPasses: boolean; viewAllActivity: boolean };
  }) {
    setEditing({
      id: o.id,
      name: o.name,
      type: o.type,
      viewAllPasses: o.permissions.viewAllPasses,
      viewAllActivity: o.permissions.viewAllActivity,
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nameValid || !slugValid) return;
    create.mutate({
      name: name.trim(),
      slug,
      type,
      permissions: { viewAllPasses, viewAllActivity },
    });
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">
            Organisations
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Toutes les résidences et immeubles de bureaux. Créez-en une, puis
            basculez dedans pour la gérer.
          </p>
        </div>
        <Button onClick={openDialog}>
          <Plus />
          Nouvelle organisation
        </Button>
      </div>

      <div className="flex flex-col gap-2.5">
        {orgs.map((o) => {
          const isActive = o.id === me.activeOrganizationId;
          const canSwitch = memberIds.has(o.id) && !isActive;
          const meta = (
            <div className="flex items-center gap-3">
              <span className="rounded-full border border-neutral-200 px-2.5 py-0.5 text-[11px] font-medium text-neutral-600">
                {typeLabel(o.type)}
              </span>
              {isActive ? (
                <span className="rounded-full bg-neutral-900 px-2.5 py-0.5 text-[11px] font-medium text-white">
                  Active
                </span>
              ) : null}
              {o.status === "inactive" ? (
                <span className="text-destructive rounded-full bg-(--danger-50) px-2.5 py-0.5 text-[11px] font-medium">
                  Suspendue
                </span>
              ) : null}
              <span
                role="button"
                tabIndex={0}
                aria-label={`Renommer ${o.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setRenaming({ id: o.id, name: o.name });
                  setRenameValue(o.name);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setRenaming({ id: o.id, name: o.name });
                    setRenameValue(o.name);
                  }
                }}
                className="cursor-pointer rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
              >
                Renommer
              </span>
              {o.status === "inactive" ? (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Réactiver ${o.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    activateOrg.mutate({ id: o.id });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      activateOrg.mutate({ id: o.id });
                    }
                  }}
                  className="cursor-pointer rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
                >
                  Réactiver
                </span>
              ) : (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Suspendre ${o.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToSuspend({ id: o.id, name: o.name, type: o.type });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setToSuspend({ id: o.id, name: o.name, type: o.type });
                    }
                  }}
                  className="cursor-pointer rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
                >
                  Suspendre
                </span>
              )}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(o);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    openEdit(o);
                  }
                }}
                className="cursor-pointer rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
              >
                Permissions
              </span>
              <span
                role="button"
                tabIndex={0}
                aria-label={`Modules de ${o.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setManagingModules({ id: o.id, name: o.name });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setManagingModules({ id: o.id, name: o.name });
                  }
                }}
                className="cursor-pointer rounded-md border border-neutral-200 px-2 py-1 text-[12px] text-neutral-600 transition-colors hover:bg-neutral-50"
              >
                Modules
              </span>
              <span className="text-[12px] text-neutral-400">
                {fmtDate(o.createdAt)}
              </span>
              {o.deletedAt ? (
                <span className="text-destructive rounded-md bg-(--danger-50) px-2 py-1 text-[12px] font-medium">
                  Suppression le {o.purgeAt ? fmtDate(o.purgeAt) : "—"}
                </span>
              ) : isActive ? null : (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Supprimer ${o.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDelete({ id: o.id, name: o.name });
                    setConfirmName("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setToDelete({ id: o.id, name: o.name });
                      setConfirmName("");
                    }
                  }}
                  className="text-destructive flex size-7 cursor-pointer items-center justify-center rounded-md border border-neutral-200 transition-colors hover:border-(--danger-500)/40 hover:bg-(--danger-50)"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.85} />
                </span>
              )}
              {canSwitch ? (
                <ChevronRight className="size-4 text-neutral-400" />
              ) : null}
            </div>
          );
          const body = (
            <>
              <div className="min-w-0">
                <div className="truncate text-[14px] font-semibold text-neutral-950">
                  {o.name}
                </div>
                <div className="mt-0.5 font-mono text-[12px] text-neutral-500">
                  {o.slug}
                </div>
              </div>
              {meta}
            </>
          );
          return canSwitch ? (
            <button
              key={o.id}
              type="button"
              onClick={() => void switchOrg(o.id)}
              className="flex items-center justify-between rounded-[14px] border border-neutral-200 bg-white px-5 py-4 text-left transition-colors hover:border-neutral-300 hover:bg-neutral-50"
            >
              {body}
            </button>
          ) : (
            <div
              key={o.id}
              className="flex items-center justify-between rounded-[14px] border border-neutral-200 bg-white px-5 py-4"
            >
              {body}
            </div>
          );
        })}
        {orgs.length === 0 ? (
          <p className="text-sm text-neutral-400">Aucune organisation.</p>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Nouvelle organisation</DialogTitle>
              <DialogDescription>
                Vous en devenez propriétaire. Le branding se règle ensuite dans
                Réglages.
              </DialogDescription>
            </DialogHeader>

            <div className="my-4 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="org-name">Nom</Label>
                <Input
                  id="org-name"
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Résidence Al Boustane"
                  maxLength={120}
                  required
                  autoFocus
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="org-slug">Slug</Label>
                <Input
                  id="org-slug"
                  value={slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="al-boustane"
                  maxLength={40}
                  aria-invalid={
                    slugError || (slug.length > 0 && !slugValid)
                      ? "true"
                      : undefined
                  }
                  required
                />
                {slugError ? (
                  <p className="text-sm text-red-600">{slugError}</p>
                ) : slug.length > 0 && !slugValid ? (
                  <p className="text-sm text-red-600">
                    Minuscules, chiffres et tirets uniquement (2–40 caractères).
                  </p>
                ) : (
                  <p className="text-[12px] text-neutral-400">
                    Identifiant d'URL. Suggéré depuis le nom, modifiable.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="org-type">Type</Label>
                <Select
                  value={type}
                  onValueChange={(v) => setType(v as "residence" | "office")}
                >
                  <SelectTrigger id="org-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residence">Résidence</SelectItem>
                    <SelectItem value="office">Immeuble de bureaux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <PermToggle
                id="perm-passes"
                label={`${capitalize(createTerms.theAdmin)} voit toutes les invitations`}
                hint={`Sinon, il ne voit que les invitations de ses propres ${createTerms.unitPlural}.`}
                checked={viewAllPasses}
                onCheckedChange={setViewAllPasses}
              />
              <PermToggle
                id="perm-activity"
                label={`${capitalize(createTerms.theAdmin)} voit toute l'activité`}
                hint={`Accès au journal d'entrées de chaque ${createTerms.unitSingular}.`}
                checked={viewAllActivity}
                onCheckedChange={setViewAllActivity}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={!nameValid || !slugValid || create.isPending}
              >
                {create.isPending ? "Création…" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permissions {editTerms.ofTheAdmin}</DialogTitle>
            <DialogDescription>
              {editing?.name} — ce que {editTerms.theAdmin} peut consulter dans{" "}
              {editTerms.thisOrg}.
            </DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="my-4 flex flex-col gap-4">
              <PermToggle
                id="edit-perm-passes"
                label={`${capitalize(editTerms.theAdmin)} voit toutes les invitations`}
                hint={`Sinon, il ne voit que les invitations de ses propres ${editTerms.unitPlural}.`}
                checked={editing.viewAllPasses}
                onCheckedChange={(v) =>
                  setEditing((e) => (e ? { ...e, viewAllPasses: v } : e))
                }
              />
              <PermToggle
                id="edit-perm-activity"
                label={`${capitalize(editTerms.theAdmin)} voit toute l'activité`}
                hint={`Accès au journal d'entrées de chaque ${editTerms.unitSingular}.`}
                checked={editing.viewAllActivity}
                onCheckedChange={(v) =>
                  setEditing((e) => (e ? { ...e, viewAllActivity: v } : e))
                }
              />
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditing(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={updatePerms.isPending}
              onClick={() => {
                if (!editing) return;
                updatePerms.mutate({
                  id: editing.id,
                  permissions: {
                    viewAllPasses: editing.viewAllPasses,
                    viewAllActivity: editing.viewAllActivity,
                  },
                });
              }}
            >
              {updatePerms.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={managingModules !== null}
        onOpenChange={(o) => {
          if (!o) setManagingModules(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modules</DialogTitle>
            <DialogDescription>
              {managingModules?.name} — les fonctionnalités activées pour
              cette organisation.
            </DialogDescription>
          </DialogHeader>

          {managingModules ? (
            <div className="my-4">
              <OrgModules orgId={managingModules.id} />
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setManagingModules(null)}
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renaming !== null}
        onOpenChange={(o) => {
          if (!o) {
            setRenaming(null);
            setRenameValue("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renommer l'organisation</DialogTitle>
            <DialogDescription>
              Le nom affiché partout dans l'app. L'identifiant d'URL (slug) ne
              change pas.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 flex flex-col gap-1.5">
            <Label htmlFor="rename-org">Nom</Label>
            <Input
              id="rename-org"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={120}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenaming(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={
                renameOrg.isPending ||
                renameValue.trim().length < 1 ||
                renameValue.trim().length > 120
              }
              onClick={() => {
                if (renaming) {
                  renameOrg.mutate({
                    id: renaming.id,
                    name: renameValue.trim(),
                  });
                }
              }}
            >
              {renameOrg.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toSuspend !== null}
        onOpenChange={(o) => {
          if (!o) setToSuspend(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre cette organisation ?</DialogTitle>
            <DialogDescription>
              {capitalize(suspendTerms.theAdmin)}, les{" "}
              {suspendTerms.ownerPlural.toLowerCase()} et les{" "}
              {suspendTerms.guardPlural.toLowerCase()} de{" "}
              <b className="font-semibold text-neutral-700">
                {toSuspend?.name}
              </b>{" "}
              n'auront plus accès à l'app ni au tableau de bord jusqu'à
              réactivation. Vous pouvez réactiver à tout moment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setToSuspend(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={suspendOrg.isPending}
              onClick={() => {
                if (toSuspend) suspendOrg.mutate({ id: toSuspend.id });
              }}
            >
              {suspendOrg.isPending ? "Suspension…" : "Suspendre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toDelete !== null}
        onOpenChange={(o) => {
          if (!o) {
            setToDelete(null);
            setConfirmName("");
          }
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <span className="text-destructive mb-2 flex size-10 items-center justify-center rounded-xl bg-(--danger-50)">
              <Trash2 className="size-5" strokeWidth={1.85} />
            </span>
            <DialogTitle className="text-lg">
              Supprimer cette organisation ?
            </DialogTitle>
            <DialogDescription>
              Toutes les données de{" "}
              <b className="font-semibold text-neutral-700">{toDelete?.name}</b>{" "}
              seront définitivement supprimées dans 14 jours. Cette action est
              irréversible.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 flex flex-col gap-1.5">
            <Label htmlFor="confirm-org-name">
              Tapez le nom de l'organisation pour confirmer
            </Label>
            <Input
              id="confirm-org-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={toDelete?.name ?? ""}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setToDelete(null);
                setConfirmName("");
              }}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={
                deleteOrg.isPending ||
                confirmName.trim() !== (toDelete?.name.trim() ?? "\0")
              }
              onClick={() => {
                if (toDelete) {
                  deleteOrg.mutate({
                    id: toDelete.id,
                    confirmName: confirmName.trim(),
                  });
                }
              }}
            >
              {deleteOrg.isPending ? "Suppression…" : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
